/**
 * Componente: InboxesTab
 * Descrição: Aba de Inboxes da tela de Gerenciamento do Tenant
 * 
 * Exibe:
 * - Cards de resumo (total, ativas, por canal)
 * - Grid/Lista de inboxes com status, canal, provedor
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação inicial (Fase 4)
 */

import { 
  Inbox, 
  CheckCircle, 
  XCircle,
  MessageSquare,
  Phone,
  Mail,
  Globe,
  Wifi,
  WifiOff,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { TenantInboxSummary } from "@/hooks/useTenantManagement";

// ============================================================================
// INTERFACES
// ============================================================================

interface InboxesTabProps {
  inboxes: TenantInboxSummary[];
  maxInboxes: number;
  isEnterprise: boolean;
  isLoading?: boolean;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Ícone do canal
 */
const getChannelIcon = (channelTypeId: string | null) => {
  switch (channelTypeId) {
    case "whatsapp":
    case "whatsapp_official":
      return MessageSquare;
    case "telegram":
      return MessageSquare;
    case "instagram":
      return MessageSquare;
    case "messenger":
      return MessageSquare;
    case "email":
      return Mail;
    case "phone":
    case "sms":
      return Phone;
    case "webchat":
      return Globe;
    default:
      return Inbox;
  }
};

/**
 * Cor do canal
 */
const getChannelColor = (channelTypeId: string | null) => {
  switch (channelTypeId) {
    case "whatsapp":
    case "whatsapp_official":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "telegram":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
    case "instagram":
      return "bg-pink-500/10 text-pink-600 dark:text-pink-400";
    case "messenger":
      return "bg-blue-600/10 text-blue-700 dark:text-blue-400";
    case "email":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
    default:
      return "bg-muted text-muted-foreground";
  }
};

/**
 * Card de resumo
 */
const SummaryCard = ({
  title,
  value,
  icon: Icon,
  variant = "default",
  isLoading,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  variant?: "default" | "success" | "warning" | "muted";
  isLoading?: boolean;
}) => {
  const variantStyles = {
    default: "bg-primary/10 text-primary",
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    muted: "bg-muted text-muted-foreground",
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div>
              <Skeleton className="h-4 w-20 mb-1" />
              <Skeleton className="h-6 w-12" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg",
            variantStyles[variant]
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Card individual de Inbox
 */
const InboxCard = ({ inbox }: { inbox: TenantInboxSummary }) => {
  const navigate = useNavigate();
  const Icon = getChannelIcon(inbox.channel_type_id);
  const colorClass = getChannelColor(inbox.channel_type_id);
  
  const isActive = inbox.status === "active";
  const isConnected = inbox.status === "active"; // Simplificado

  return (
    <Card 
      className={cn(
        "relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer",
        !isActive && "opacity-60"
      )}
      onClick={() => navigate(`/tenant/inboxes/${inbox.id}`)}
    >
      {/* Indicador de status no topo */}
      <div className={cn(
        "absolute inset-x-0 top-0 h-1",
        isActive ? "bg-emerald-500" : "bg-muted-foreground/30"
      )} />
      
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Ícone e Info */}
          <div className="flex items-start gap-3">
            <div className={cn(
              "flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0",
              colorClass
            )}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h4 className="font-medium text-sm truncate">{inbox.name}</h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                {inbox.channel_type?.display_name || inbox.channel_type_id || "Canal"}
              </p>
              {inbox.provider_display_name && (
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  via {inbox.provider_display_name}
                </p>
              )}
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex flex-col items-end gap-1">
            {isConnected ? (
              <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0 text-xs gap-1">
                <Wifi className="h-3 w-3" />
                Conectada
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-muted text-muted-foreground border-0 text-xs gap-1">
                <WifiOff className="h-3 w-3" />
                Desconectada
              </Badge>
            )}
          </div>
        </div>

        {/* Footer com data */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t text-xs text-muted-foreground">
          <span>
            Criada em {format(new Date(inbox.created_at), "dd/MM/yyyy", { locale: ptBR })}
          </span>
          <ExternalLink className="h-3.5 w-3.5" />
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function InboxesTab({ inboxes, maxInboxes, isEnterprise, isLoading }: InboxesTabProps) {
  const navigate = useNavigate();
  
  // Contagens
  const totalInboxes = inboxes.length;
  const activeInboxes = inboxes.filter((i) => i.status === "active").length;
  const suspendedInboxes = inboxes.filter((i) => i.status === "suspended").length;

  // Agrupar por canal
  const channelCounts = inboxes.reduce((acc, inbox) => {
    const channel = inbox.channel_type?.display_name || inbox.channel_type_id || "Outro";
    acc[channel] = (acc[channel] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total de Inboxes"
          value={`${totalInboxes} / ${maxInboxes}`}
          icon={Inbox}
          variant="default"
          isLoading={isLoading}
        />
        <SummaryCard
          title="Ativas"
          value={activeInboxes}
          icon={CheckCircle}
          variant="success"
          isLoading={isLoading}
        />
        <SummaryCard
          title="Suspensas"
          value={suspendedInboxes}
          icon={XCircle}
          variant="warning"
          isLoading={isLoading}
        />
        <SummaryCard
          title="Canais Usados"
          value={Object.keys(channelCounts).length}
          icon={MessageSquare}
          variant="muted"
          isLoading={isLoading}
        />
      </div>

      {/* Grid de Inboxes */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Conexões Configuradas</h3>
            <p className="text-sm text-muted-foreground">
              Clique em uma inbox para ver detalhes
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/tenant/inboxes")}
          >
            Gerenciar Inboxes
          </Button>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : inboxes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Nenhuma inbox configurada
              </p>
              <Button onClick={() => navigate("/tenant/inboxes")}>
                Criar Primeira Inbox
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inboxes.map((inbox) => (
              <InboxCard key={inbox.id} inbox={inbox} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
